'use client';

import React, { useMemo } from 'react';

interface VoiceWaveformProps {
  isActive: boolean;
  audioLevel: number;
  size?: 'sm' | 'md' | 'lg';
  barCount?: number;
  label?: string;
}

const sizeConfig = {
  sm: { height: 'h-8', barWidth: 'w-1', gap: 'gap-0.5', text: 'text-xs', maxBarHeight: 24 },
  md: { height: 'h-12', barWidth: 'w-1.5', gap: 'gap-1', text: 'text-sm', maxBarHeight: 40 },
  lg: { height: 'h-16', barWidth: 'w-2', gap: 'gap-1', text: 'text-sm', maxBarHeight: 56 },
};

/**
 * User speaking indicator - waveform bars style
 */
export function VoiceWaveform({
  isActive,
  audioLevel,
  size = 'md',
  barCount = 5,
  label,
}: VoiceWaveformProps) {
  const config = sizeConfig[size];

  const barHeights = useMemo(() => {
    const minHeight = 6;
    if (!isActive || audioLevel < 5) {
      return Array(barCount).fill(minHeight);
    }

    const baseHeight = minHeight + (audioLevel / 100) * (config.maxBarHeight - minHeight);
    return Array(barCount)
      .fill(0)
      .map((_, i) => {
        const position = Math.abs(i - Math.floor(barCount / 2));
        const variation = 1 - position * 0.12;
        const randomFactor = 0.7 + Math.random() * 0.6;
        return Math.max(minHeight, baseHeight * variation * randomFactor);
      });
  }, [isActive, audioLevel, barCount, config.maxBarHeight]);

  const isListening = isActive && audioLevel > 5;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className={`flex items-center justify-center ${config.gap} ${config.height}`}>
        {barHeights.map((height, index) => (
          <div
            key={index}
            className={`${config.barWidth} rounded-full transition-all duration-75 ${
              isListening
                ? 'bg-[color:var(--primary-500)]'
                : 'bg-[color:var(--gray-400)]'
            }`}
            style={{ height: `${height}px` }}
          />
        ))}
      </div>

      {label && (
        <span className={`${config.text} font-medium ${isListening ? 'text-[color:var(--primary-600)]' : 'text-[color:var(--gray-500)]'}`}>
          {label}
        </span>
      )}
    </div>
  );
}
